import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Text,
  useColorModeValue,
  Flex,
  Image,
  VStack,
  Badge,
  useToast,
  HStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ScaleFade,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Tooltip,
  Divider,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  StatGroup
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { useWallet } from '../context/WalletContext';
import blindboxService, { BlindboxItem } from '../services/blindboxService';

// Local storage keys
const STORAGE_KEY_SHOW_RESULT = 'blindbox_show_result';
const STORAGE_KEY_RESULT_ITEMS = 'blindbox_result_items';
const STORAGE_KEY_QUANTITY = 'blindbox_quantity';

interface BlindboxCardProps {
  cost: number;
  onPurchaseComplete: () => void;
}

const BlindboxCard: React.FC<BlindboxCardProps> = ({ 
  cost, 
  onPurchaseComplete 
}) => {
  const { walletAddress, userBalance, refreshBalance } = useWallet();
  const [isLoading, setIsLoading] = useState(false);
  const [quantity, setQuantity] = useState<number>(() => {
    // Initialize from localStorage if available
    try {
      const savedQuantity = localStorage.getItem(STORAGE_KEY_QUANTITY);
      return savedQuantity ? parseInt(savedQuantity, 10) || 1 : 1;
    } catch (e) {
      console.error('Error reading quantity from localStorage:', e);
      return 1;
    }
  });
  const [showResult, setShowResult] = useState<boolean>(() => {
    // Initialize from localStorage if available
    try {
      const savedShowResult = localStorage.getItem(STORAGE_KEY_SHOW_RESULT);
      return savedShowResult ? JSON.parse(savedShowResult) : false;
    } catch (e) {
      console.error('Error reading modal state from localStorage:', e);
      return false;
    }
  });
  
  const [resultItems, setResultItems] = useState<BlindboxItem[]>(() => {
    // Initialize from localStorage if available
    try {
      const savedResultItems = localStorage.getItem(STORAGE_KEY_RESULT_ITEMS);
      return savedResultItems ? JSON.parse(savedResultItems) : [];
    } catch (e) {
      console.error('Error reading result items from localStorage:', e);
      return [];
    }
  });
  
  const toast = useToast();
  
  // Use refs to maintain state across renders
  const hasNotifiedParent = useRef(false);
  const purchaseCompleted = useRef(false);
  
  const [stats, setStats] = useState<any>(null);
  
  // Save state to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_SHOW_RESULT, JSON.stringify(showResult));
    } catch (e) {
      console.error('Error saving modal state to localStorage:', e);
    }
  }, [showResult]);
  
  useEffect(() => {
    if (resultItems && resultItems.length > 0) {
      try {
        localStorage.setItem(STORAGE_KEY_RESULT_ITEMS, JSON.stringify(resultItems));
      } catch (e) {
        console.error('Error saving result items to localStorage:', e);
      }
    }
  }, [resultItems]);
  
  // Save quantity to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_QUANTITY, quantity.toString());
    } catch (e) {
      console.error('Error saving quantity to localStorage:', e);
    }
  }, [quantity]);
  
  // Fetch stats on mount to get expected values
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const statsData = await blindboxService.getBlindboxStats();
        setStats(statsData);
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    };
    
    fetchStats();
  }, []);
  
  // Effect to notify parent of purchase complete without closing the modal
  useEffect(() => {
    if (purchaseCompleted.current && !hasNotifiedParent.current && resultItems.length > 0) {
      console.log("Notifying parent of purchase completion while keeping modal open");
      hasNotifiedParent.current = true;
      
      // Call onPurchaseComplete without affecting the modal
      if (typeof onPurchaseComplete === 'function') {
        // Add a small delay to prevent rapid consecutive calls
        setTimeout(() => {
          onPurchaseComplete();
        }, 300);
      }
    }
    
    return () => {
      // Reset the notification flag if the modal is closed
      if (!showResult) {
        hasNotifiedParent.current = false;
      }
    };
  }, [resultItems, showResult, onPurchaseComplete]);

  // Get color based on item rarity
  const getItemColor = (name: string) => {
    switch (name) {
      case 'Common':
        return 'gray';
      case 'Standard':
        return 'blue';
      case 'Uncommon':
        return 'green';
      case 'Rare':
        return 'purple';
      case 'Epic':
        return 'pink';
      case 'Legendary':
        return 'orange';
      default:
        return 'teal';
    }
  };

  // Animation keyframes for the blindbox reveal
  const pulseAnimation = keyframes`
    0% { transform: scale(1); }
    50% { transform: scale(1.05); }
    100% { transform: scale(1); }
  `;

  const pulse = `${pulseAnimation} 0.5s ease-in-out infinite`;

  // Calculate total cost
  const totalCost = cost * quantity;
  
  // Calculate expected values and profit/loss
  const normalEV = stats?.stats?.normalExpectedValue || 0.095; // Default to config value
  const adjustedEV = stats?.stats?.adjustedExpectedValue || 0.07; // Default to config value
  
  // Use normal or adjusted EV based on current pool state
  const currentEV = stats?.poolInfo?.state === 'Adjusted' ? adjustedEV : normalEV;
  
  // Calculate expected return and profit/loss
  const expectedReturn = currentEV * quantity;
  const expectedProfitLoss = expectedReturn - totalCost;
  const isProfitable = expectedProfitLoss >= 0;
  
  // Calculate total reward from result items
  const totalReward = resultItems.reduce((sum, item) => sum + item.value, 0);

  // Handle blindbox purchase
  const handlePurchase = async () => {
    // First validate that wallet is connected
    if (!walletAddress) {
      toast({
        title: 'Wallet not connected',
        description: "Please connect your wallet first.",
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (userBalance < totalCost) {
      toast({
        title: 'Insufficient balance',
        description: `You need at least ${totalCost.toFixed(2)} SOL to purchase ${quantity} blindbox${quantity !== 1 ? 'es' : ''}.`,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsLoading(true);
      purchaseCompleted.current = false;
      hasNotifiedParent.current = false;
      
      // Call the API with the selected quantity
      const result = await blindboxService.purchaseBlindbox(walletAddress, quantity);
      
      // Refresh user balance
      await refreshBalance();
      
      // Show results - handle both single and multiple results
      const items = Array.isArray(result) 
        ? result.map(r => r.item) 
        : [result.item];
      
      setResultItems(items);
      setShowResult(true);
      purchaseCompleted.current = true;
      
      // Parent notification will happen via the useEffect
    } catch (error) {
      console.error('Error purchasing blindbox:', error);
      toast({
        title: 'Purchase failed',
        description: "There was an error processing your purchase.",
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      purchaseCompleted.current = false;
    } finally {
      setIsLoading(false);
    }
  };

  // Handle modal close - only when explicitly requested by user
  const handleCloseResult = () => {
    console.log("Modal explicitly closed by user");
    setShowResult(false);
    // Also clear localStorage when explicitly closed
    try {
      localStorage.removeItem(STORAGE_KEY_SHOW_RESULT);
      localStorage.removeItem(STORAGE_KEY_RESULT_ITEMS);
      // Don't remove quantity from localStorage as we want to persist it
    } catch (e) {
      console.error('Error clearing localStorage:', e);
    }
    
    // Don't reset the result items so they can be shown again if needed
    purchaseCompleted.current = false;
    hasNotifiedParent.current = false;
  };

  // Handle opening another box from the result modal
  const handleOpenAnotherBox = async () => {
    // Early return if wallet is not connected
    if (!walletAddress) {
      toast({
        title: 'Wallet not connected',
        description: "Please connect your wallet first.",
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    setIsLoading(true);
    
    try {
      purchaseCompleted.current = false;
      hasNotifiedParent.current = false;
      
      // Purchase another blindbox with the current quantity value
      const result = await blindboxService.purchaseBlindbox(walletAddress, quantity);
      
      // Refresh user balance
      await refreshBalance();
      
      // Update result and keep modal open
      const newItems = Array.isArray(result) ? result.map(r => r.item) : [result.item];
      setResultItems(newItems); // Replace with new result(s)
      purchaseCompleted.current = true;
      
      // Don't need to set showResult again since the modal is already open
    } catch (error) {
      console.error('Error purchasing another blindbox:', error);
      toast({
        title: 'Purchase failed',
        description: "There was an error processing your purchase.",
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Box
        maxW="sm"
        borderWidth="1px"
        borderRadius="lg"
        overflow="hidden"
        bg={useColorModeValue('white', 'gray.700')}
        boxShadow="md"
        p={4}
      >
        <VStack spacing={4}>
          <Image
            src="https://via.placeholder.com/300x200?text=Mystery+Box"
            alt="BlindBox"
            borderRadius="md"
            w="100%"
          />
          
          <Text fontSize="xl" fontWeight="bold">
            Mystery BlindBox
          </Text>
          
          <Badge colorScheme="brand" fontSize="md" px={3} py={1}>
            {cost.toFixed(2)} SOL per box
          </Badge>
          
          <Text fontSize="sm" textAlign="center" color="gray.500">
            Open this box to win SOL! Each box contains a random reward worth between 0.05 and 0.5 SOL.
          </Text>
          
          <HStack w="full">
            <Text>Quantity:</Text>
            <NumberInput 
              min={1} 
              max={100}
              value={quantity}
              onChange={(_, val) => setQuantity(val)}
              size="md"
              maxW="100px"
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
          </HStack>
          
          {/* Expected Win/Loss Stats */}
          <Box 
            w="full" 
            p={3} 
            borderRadius="md" 
            bg={useColorModeValue('gray.50', 'gray.700')}
          >
            <StatGroup>
              <Stat>
                <StatLabel>Expected Return</StatLabel>
                <StatNumber fontSize="md">{expectedReturn.toFixed(3)} SOL</StatNumber>
                <StatHelpText>
                  {quantity} × {currentEV.toFixed(3)} SOL
                </StatHelpText>
              </Stat>
              
              <Stat>
                <StatLabel>Estimated {isProfitable ? 'Profit' : 'Loss'}</StatLabel>
                <StatNumber 
                  fontSize="md" 
                  color={isProfitable ? 'green.500' : 'red.500'}
                >
                  <StatArrow type={isProfitable ? 'increase' : 'decrease'} />
                  {Math.abs(expectedProfitLoss).toFixed(3)} SOL
                </StatNumber>
                <StatHelpText>
                  {expectedReturn.toFixed(2)} - {totalCost.toFixed(2)} SOL
                </StatHelpText>
              </Stat>
            </StatGroup>
          </Box>
          
          <Tooltip 
            label={userBalance < totalCost ? `You need ${totalCost.toFixed(2)} SOL` : ""}
            isDisabled={userBalance >= totalCost}
          >
            <Button
              colorScheme="brand"
              size="lg"
              width="full"
              onClick={handlePurchase}
              isLoading={isLoading}
              loadingText="Processing..."
              isDisabled={!walletAddress || userBalance < totalCost}
            >
              Buy {quantity > 1 ? `${quantity} Boxes` : "& Open Now"} - {totalCost.toFixed(2)} SOL
            </Button>
          </Tooltip>
        </VStack>
      </Box>

      {/* Result Modal - Persisted across renders */}
      <Modal 
        isOpen={showResult} 
        onClose={handleCloseResult} 
        isCentered 
        size={resultItems.length > 1 ? "xl" : "md"}
        closeOnOverlayClick={false} // Prevent closing by clicking outside
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader textAlign="center">
            BlindBox Results
            {resultItems.length > 0 && (
              <Flex direction="column" align="center" mt={2}>
                <HStack spacing={2}>
                  <Text fontSize="sm">You paid:</Text>
                  <Badge colorScheme="gray">
                    {(cost * (resultItems.length === 1 ? 1 : resultItems.length)).toFixed(2)} SOL
                  </Badge>
                </HStack>
                
                <HStack spacing={2} mt={1}>
                  <Text fontSize="sm">Total reward:</Text>
                  <Badge colorScheme="green" p={1} fontSize="sm">
                    {totalReward.toFixed(2)} SOL
                  </Badge>
                </HStack>
                <Button
                    mt={6}
                    colorScheme="brand"
                    onClick={handleOpenAnotherBox}
                    isLoading={isLoading}
                    loadingText="Processing..."
                    isDisabled={!walletAddress || userBalance < totalCost}
                  >
                    Open {quantity > 1 ? `${quantity} Boxes` : "Another Box"}
                  </Button>
              </Flex>
            )}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {resultItems.length > 0 && (
              <ScaleFade initialScale={0.9} in={showResult}>
                <Flex direction="column" align="center" justify="center" py={6}>
                  {resultItems.length === 1 ? (
                    // Single result display
                    <Box 
                      animation={pulse} 
                      bg={`${getItemColor(resultItems[0]?.name || 'Common')}.100`} 
                      p={6} 
                      borderRadius="lg"
                      mb={4}
                      textAlign="center"
                    >
                      <Badge colorScheme={getItemColor(resultItems[0]?.name || 'Common')} px={3} py={1} fontSize="md" mb={2}>
                        {resultItems[0].name}
                      </Badge>
                      <Text fontSize="4xl" fontWeight="bold" color={`${getItemColor(resultItems[0]?.name || 'Common')}.500`}>
                        {resultItems[0].value.toFixed(2)} SOL
                      </Text>
                    </Box>
                  ) : (
                    // Multiple results display
                    <Box width="100%" mb={4}>
                      <Table variant="simple" size="sm">
                        <Thead>
                          <Tr>
                            <Th>#</Th>
                            <Th>Rarity</Th>
                            <Th>Reward</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {resultItems.map((item, index) => (
                            <Tr key={index}>
                              <Td>{index + 1}</Td>
                              <Td>
                                <Badge colorScheme={getItemColor(item.name)}>
                                  {item.name}
                                </Badge>
                              </Td>
                              <Td isNumeric fontWeight="bold">
                                {item.value.toFixed(2)} SOL
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                  )}
                  
                  <Divider my={4} />
                  
                  
                </Flex>
              </ScaleFade>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default BlindboxCard; 